import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Cookie, X } from "lucide-react";
import { useConsent } from "@/context/ConsentContext";
import { useI18n } from "@/lib/i18n";

export default function CookieBanner() {
  const { prefs, acceptAll, rejectAll, savePrefs, managerOpen, setManagerOpen } = useConsent();
  const { lang } = useI18n();
  const [custom, setCustom] = useState(false);
  const [local, setLocal] = useState({ analytics: prefs.analytics, advertising: prefs.advertising });

  useEffect(() => { setLocal({ analytics: prefs.analytics, advertising: prefs.advertising }); }, [prefs, managerOpen]);

  const show = !prefs.decided || managerOpen;
  if (!show) return null;

  const t = (it, en) => (lang === "it" ? it : en);
  const showPanel = custom || managerOpen;

  return (
    <div className="fixed inset-x-0 bottom-0 z-[60] p-3 sm:p-5" data-testid="cookie-banner">
      <div className="max-w-4xl mx-auto glass border border-olive/25 rounded-2xl shadow-2xl overflow-hidden">
        <div className="p-5 sm:p-6">
          <div className="flex items-start gap-3">
            <span className="w-10 h-10 rounded-full bg-olive/15 text-olive-dark flex items-center justify-center shrink-0"><Cookie size={20} /></span>
            <div className="flex-1">
              <h3 className="font-serif text-xl">{t("Rispettiamo la tua privacy", "We value your privacy")}</h3>
              <p className="mt-1 text-sm text-charcoal/70 leading-relaxed">
                {t(
                  "Usiamo cookie necessari per il funzionamento del sito e, solo con il tuo consenso, cookie statistici e pubblicitari. Puoi accettare, rifiutare o personalizzare le tue scelte.",
                  "We use necessary cookies for the site to work and, only with your consent, statistics and advertising cookies. You can accept, reject or customize your choices."
                )}{" "}
                <Link to="/cookie-policy" className="text-olive-dark underline font-medium">Cookie Policy</Link>
              </p>

              {showPanel && (
                <div className="mt-4 space-y-2" data-testid="cookie-preferences">
                  <label className="flex items-center justify-between bg-white/70 rounded-lg px-4 py-2.5 border border-olive/15">
                    <span className="text-sm font-medium">{t("Necessari", "Necessary")} <span className="text-charcoal/40 font-normal">— {t("sempre attivi", "always on")}</span></span>
                    <input type="checkbox" checked disabled className="accent-olive" />
                  </label>
                  <label className="flex items-center justify-between bg-white/70 rounded-lg px-4 py-2.5 border border-olive/15 cursor-pointer">
                    <span className="text-sm font-medium">{t("Statistici", "Analytics")}</span>
                    <input type="checkbox" data-testid="consent-analytics" checked={local.analytics} onChange={(e) => setLocal({ ...local, analytics: e.target.checked })} className="accent-olive" />
                  </label>
                  <label className="flex items-center justify-between bg-white/70 rounded-lg px-4 py-2.5 border border-olive/15 cursor-pointer">
                    <span className="text-sm font-medium">{t("Pubblicitari", "Advertising")}</span>
                    <input type="checkbox" data-testid="consent-advertising" checked={local.advertising} onChange={(e) => setLocal({ ...local, advertising: e.target.checked })} className="accent-olive" />
                  </label>
                </div>
              )}

              <div className="mt-4 flex flex-wrap gap-2">
                <button onClick={acceptAll} data-testid="cookie-accept-all" className="rounded-full bg-olive hover:bg-olive-dark text-white font-semibold px-5 py-2.5 text-sm transition-colors">{t("Accetta tutti", "Accept all")}</button>
                <button onClick={rejectAll} data-testid="cookie-reject-all" className="rounded-full border border-charcoal/20 hover:border-olive px-5 py-2.5 text-sm font-semibold transition-colors">{t("Rifiuta", "Reject")}</button>
                {showPanel ? (
                  <button onClick={() => savePrefs(local)} data-testid="cookie-save" className="rounded-full border border-olive text-olive-dark hover:bg-olive/10 px-5 py-2.5 text-sm font-semibold transition-colors">{t("Salva preferenze", "Save preferences")}</button>
                ) : (
                  <button onClick={() => setCustom(true)} data-testid="cookie-customize" className="rounded-full border border-charcoal/20 hover:border-olive px-5 py-2.5 text-sm font-semibold transition-colors">{t("Personalizza", "Customize")}</button>
                )}
              </div>
            </div>
            {managerOpen && (
              <button onClick={() => setManagerOpen(false)} className="p-1 text-charcoal/40 hover:text-charcoal" aria-label="Close"><X size={18} /></button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
